// Converted to typescript through help of ChatGPT
import { CoreAction, CoreActionCreator } from 'app-components/flux/core/resourceStoreTypes';
import { call, put, race, take, takeEvery, takeLeading } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from '../core';

import { getLogger } from '../../utils/core';
import * as actions from './documentsActions';
import { getLastSyncDate } from './documentsSelectors';
import {
  transformDocumentToRequestData,
  transformDocumentWithResponse,
  transformResponseToDocuments,
} from './documentsTransformers';
import { mkDocument } from './documentsTypes';

const log = getLogger('flux/documents - documentsSagas');

const mkResourceConfig = (overrides: {
  successAction: CoreActionCreator<any, any>;
  failureAction: CoreActionCreator<any, any>;
  resourceBaseUrl?: string | ((action: any) => string) | undefined;
}) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'document',
    resourceBaseUrl: '/documents',
    getLastSyncDate,

    transformResponseToResources: transformResponseToDocuments,
    transformResourceToRequestData: transformDocumentToRequestData,
    transformResourceWithResponse: transformDocumentWithResponse,

    ...overrides,
  });

export function* getDocumentsActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getDocumentsSuccess,
    failureAction: actions.getDocumentsFailure,
  });
  yield takeLeading(actions.getDocuments.toString(), resourceSagaUtils.qcsSyncGetResources, resourceConfig as any);
  // Must add 'as any' to the last prop or else it returns an error Invalid number of arguments, expected 2
}

export function* createDocumentActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.createDocumentSuccess,
    failureAction: actions.createDocumentFailure,
  });
  yield takeEvery(actions.createDocument.toString(), resourceSagaUtils.qcsSyncCreateResource, resourceConfig as any);
}

export function* deleteDocumentActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.deleteDocumentSuccess,
    failureAction: actions.deleteDocumentFailure,
  });
  yield takeEvery(actions.deleteDocument.toString(), resourceSagaUtils.qcsSyncDeleteResource, resourceConfig as any);
}

export function* getDocumentActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getDocumentSuccess,
    failureAction: actions.getDocumentFailure,
    resourceBaseUrl: (action: CoreAction<any, any>) => `/documents/${action.payload.id}/original`,
  });
  yield takeEvery(actions.getDocument.toString(), resourceSagaUtils.qcsSyncGetResource, resourceConfig as any);
}

export function* uploadDocumentActionWatcher() {
  yield takeEvery(actions.uploadDocument, function* _uploadDocument(uploadDocumentAction: CoreAction<any, any>) {
    yield put(actions.createDocument(mkDocument(uploadDocumentAction.payload?.document)));
    const { createDocumentSuccessAction, createDocumentFailureAction } = yield race({
      createDocumentSuccessAction: take(
        (action: CoreAction<any, any>) =>
          action.type === actions.createDocumentSuccess.toString() &&
          action.payload.clientId &&
          action.payload.clientId === uploadDocumentAction.payload?.document?.clientId,
      ),
      createDocumentFailureAction: take(
        (action: CoreAction<any, any>) =>
          action.type === actions.createDocumentFailure.toString() &&
          action.payload.clientId &&
          action.payload.clientId === uploadDocumentAction.payload?.document?.clientId,
      ),
    });

    const stringResponse = JSON.stringify(createDocumentSuccessAction, null, 2);

    if (
      createDocumentSuccessAction &&
      assert(createDocumentSuccessAction.payload.url, `'url' unexpected create document response`, {
        action: stringResponse,
      }) &&
      assert(createDocumentSuccessAction.payload.contentType, `'contentType' unexpected create document response`, {
        action: stringResponse,
      }) &&
      assert(createDocumentSuccessAction.payload.id, `'id' unexpected create document response`, {
        action: stringResponse,
      })
    ) {
      let dataUploadResponseAsFetch: Response;
      try {
        // Use native fetch since axios has a polyfill issue on mobile https://github.com/axios/axios/issues/1321.
        dataUploadResponseAsFetch = yield call(fetch, createDocumentSuccessAction.payload.url, {
          method: 'PUT',
          headers: { 'Content-Type': createDocumentSuccessAction.payload.contentType },
          body: uploadDocumentAction.payload.data,
        });
      } catch (error: any) {
        log.log('Error uploading your document:', error);
        yield put(actions.deleteDocument(mkDocument({ id: createDocumentSuccessAction.payload.id })));
        yield put(
          actions.uploadDocumentFailure({
            clientId: uploadDocumentAction.payload.clientId,
            errorDescription: `${error.name} ${error.message}`,
            ...error,
          }),
        );
        uploadDocumentAction.meta?.reject?.(error);
        return;
      }

      if (dataUploadResponseAsFetch && dataUploadResponseAsFetch.ok) {
        yield put(
          actions.uploadDocumentSuccess({
            clientId: uploadDocumentAction.payload.clientId,
          }),
        );
        uploadDocumentAction.meta?.resolve?.(createDocumentSuccessAction.payload);
      } else {
        yield put(actions.deleteDocument(mkDocument({ id: createDocumentSuccessAction.payload.id })));
        yield put(
          actions.uploadDocumentFailure({
            clientId: uploadDocumentAction.payload.clientId,
            errorDescription: 'document upload',
            status: dataUploadResponseAsFetch.status,
            statusText: dataUploadResponseAsFetch.statusText,
          }),
        );
        uploadDocumentAction.meta?.reject?.(dataUploadResponseAsFetch);
      }
    } else {
      yield put(
        actions.uploadDocumentFailure({
          clientId: uploadDocumentAction.payload.clientId,
          errorDescription: 'create document placeholder',
          ...(createDocumentFailureAction ? createDocumentFailureAction.payload : createDocumentSuccessAction.payload),
        }),
      );
      uploadDocumentAction.meta?.reject?.(createDocumentFailureAction);
    }
  });
}

export function* uploadDataActionWatcher(): Generator {
  yield takeEvery(actions.uploadData.toString(), function* uploadData(action: CoreAction<any, any>): Generator {
    let result;
    try {
      // Use native fetch since axios has a polyfill issue on mobile https://github.com/axios/axios/issues/1321.
      result = yield call(fetch, action.payload.url, {
        method: 'PUT',
        headers: { 'Content-Type': action.payload.contentType },
        body: action.payload.data,
      });
      yield put(actions.uploadDataSuccess(result));
      action.meta?.resolve?.(result);
    } catch (error) {
      result = error;
      log.log('Error uploading your data:', error);
      yield put(actions.uploadDataFailure(result));
      action.meta?.reject?.(result);
    }

    return result;
  });
}

// ====================================================
// EXPORTS

export default [
  getDocumentsActionWatcher,
  createDocumentActionWatcher,
  deleteDocumentActionWatcher,
  getDocumentActionWatcher,
  uploadDocumentActionWatcher,
  uploadDataActionWatcher,
];
